import { call, put, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import apis from "#store/api";
import {
  loginUser,
  loginSuccess,
  loginFailure,
  LoginPayload,
  loadCurrentUser,
  loadCurrentUserSuccess,
  loadCurrentUserFailure,
  refreshTokenSuccess,
  refreshTokenFailure,
  logout,
  requestResetPassword,
  requestResetPasswordSuccess,
  requestResetPasswordFailure,
  refreshToken,
} from "./actions";
import { LOCAL_STORAGE_KEYS } from "#root/utils/localStorage";
import { CurrentUser } from "#root/queries/users";

// Helper function to extract error message
const getErrorMessage = (error: any): string => {
  return error.response?.data.Message || error.message || "Operation failed";
};

// Helper function to map user data
const mapUserData = (data: CurrentUser) => ({
  id: data.id,
  email: data.email,
  firstName: data.firstName,
  lastName: data.lastName,
  phoneNumber: data.phoneNumber,
  role: data.role,
  language: data.language,
  unit: data.unit,
  tenantType: data.tenantType
});

function* loginSaga(action: PayloadAction<LoginPayload>): Generator<any, void, any> {
  try {
    const { email, password, tenantId, superAdmin } = action.payload;

    // Handle tenant ID
    superAdmin ? localStorage.removeItem(LOCAL_STORAGE_KEYS.TENANT_ID) 
               : tenantId && localStorage.setItem(LOCAL_STORAGE_KEYS.TENANT_ID, tenantId);

    const response = yield call(apis.auth.login, { data: { email, password } });
    if (!response.data.isSuccess) {
      throw new Error(response.data.message);
    }

    yield put(loginSuccess());

    if (response.data.currentUser) {
      yield put(loadCurrentUserSuccess(response.data.currentUser));
    }
  } catch (error: any) {
    yield put(loginFailure({ error: getErrorMessage(error) }));
  }
}

function* fetchCurrentUser(): Generator<any, any, any> {
  const response = yield call(apis.auth.getCurrentUser);
  const user = response.data?.item;

  if (!user) {
    return null;
  }

  return mapUserData(user);
}

function* refreshTokenSaga(): Generator<any, void, any> {
  try {
    const response = yield call(apis.auth.refreshToken, { data: {} });
    if (!response.data.isSuccess) {
      throw new Error(response.data.message);
    }

    yield put(refreshTokenSuccess());

    if (response.data.currentUser) {
      yield put(loadCurrentUserSuccess(response.data.currentUser));
    }
  } catch (error: any) {
    yield put(refreshTokenFailure({ error: getErrorMessage(error) }));
    yield put(logout());
  }
}

function* loadCurrentUserSaga(): Generator<any, void, any> {
  try {
    const userData = yield call(fetchCurrentUser);
    yield put(loadCurrentUserSuccess(userData));

    // Check if any claim-relevant data has changed
    const cachedUser = localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_USER_KEY);
    const previousUser = cachedUser ? JSON.parse(cachedUser) : null;
    
    if (!previousUser || (
      previousUser.role !== userData.role ||
      previousUser.email !== userData.email ||
      previousUser.id !== userData.id
    )) {
      // Important user data changed - trigger token refresh
      yield put(refreshToken());
    }
  } catch (error: any) {
    console.log(error);
    if (error.response?.status >= 400 && error.response?.status < 500) {
      yield put(refreshTokenFailure({ error: getErrorMessage(error) }));
      yield put(logout());
    } else {
      yield put(loadCurrentUserFailure({ error: getErrorMessage(error) }));
    }
  }
}

function* requestResetPasswordSaga(
  action: PayloadAction<{ email: string; baseUrl: string; tenantId?: string; }>
): Generator<any, void, any> {
  try {
    const { email, baseUrl, tenantId } = action.payload;
    if (tenantId) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.TENANT_ID, tenantId);
    }
    yield call(apis.auth.requestResetPassword, { 
      data: { email, baseUrl }
    });
    yield put(requestResetPasswordSuccess());
  } catch (error: any) {
    yield put(requestResetPasswordFailure({ error: getErrorMessage(error) }));
  }
}

function* logoutSaga(): Generator<any, void, any> {
  try {
    yield call(apis.auth.logout);
  } catch (error: any) {
    console.error('Logout failed:', getErrorMessage(error));
  }
}

export default function* authSaga() {
  yield takeLatest(loginUser.type, loginSaga);
  yield takeLatest(loadCurrentUser.type, loadCurrentUserSaga);
  yield takeLatest(requestResetPassword.type, requestResetPasswordSaga);
  yield takeLatest(refreshToken.type, refreshTokenSaga);
  yield takeLatest(logout.type, logoutSaga);
}
